import {
	PageElementsFamily,
	createLocation,
	pageHasWidescreenState,
} from "../../state/page";
import { Suspense, lazy, memo, useEffect, useRef, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import ClapperBoard from "../../icons/clapperboard.svg?react";
import { EditMode } from "../../state/ui";
import type { ElementProps } from "../../functionalComponents/RenderPage";
import styled from "styled-components";

// Lazy load react-player to reduce initial bundle size
const ReactPlayer = lazy(() => import("react-player"));
import useAiAnimation from "../../lib/animations/aiAnimationHook";
import useTappedAnimation from "../../lib/animations/tappedAnimationHook";

function parseTrueOrYes(value: string | undefined) {
	const lowerValue = value?.toLowerCase();
	switch (lowerValue) {
		case "on":
		case "yes":
			return true;
		case "off":
		case "no":
			return false;
		default:
			return false;
	}
}

const StyledVideoWrapper = styled.div<{ $widescreen: boolean }>`
	width: 100%;
	height: 100%;
	aspect-ratio: 16/9;
	/* players are interactive: sit above the invisible tap-navigation
	   overlays or their controls are unclickable */
	position: relative;
	z-index: 101;
	${({ $widescreen }) =>
		$widescreen &&
		`
    width: 100vw;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  `}
`;

function Video({ atomId, pageIndex }: ElementProps) {
	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const inEditMode = useRecoilValue(EditMode);
	const tapped = elementData.tapped;
	const videoRef = useRef<HTMLDivElement>(null);
	const widescreen = elementData.modifier?.["widescreen:"]
		? parseTrueOrYes(elementData.modifier["widescreen:"])
		: false;

	const setPageHasWidescreen = useSetRecoilState(
		pageHasWidescreenState(pageIndex),
	);
	const [isInView, setIsInView] = useState(false);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				setIsInView(entry.isIntersecting);
			},
			{ threshold: 0.5 },
		);

		if (videoRef.current) {
			observer.observe(videoRef.current);
		}

		return () => {
			if (videoRef.current) {
				observer.unobserve(videoRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (widescreen) {
			setPageHasWidescreen(true);
		}
		return () => {
			if (widescreen) {
				setPageHasWidescreen(false);
			}
		};
	}, [widescreen, setPageHasWidescreen]);

	useTappedAnimation({ elRef: videoRef, tapped, atomId });
	useAiAnimation({ elRef: videoRef, modifier: elementData.modifier });

	if (typeof elementData.content !== "string") {
		return <></>;
	}

	if (inEditMode) {
		return (
			<ClapperBoard
				style={{
					width: "100%",
					visibility: "visible",
					aspectRatio: "16/9",
				}}
			/>
		);
	}

	return (
		<StyledVideoWrapper ref={videoRef} $widescreen={widescreen} className="video">
			<Suspense fallback={<div style={{ width: "100%", aspectRatio: "16/9", background: "#000" }} />}>
				<ReactPlayer
					controls={false}
					loop={true}
					muted={true}
					playing={isInView}
					playsinline={true}
					url={elementData.content}
					width={"100%"}
					height={"100%"}
				/>
			</Suspense>
		</StyledVideoWrapper>
	);
}

export default memo(Video);
